import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Svg,
  Defs,
  LinearGradient,
  Stop,
  Rect,
} from "@react-pdf/renderer";
import moment from "moment";
import goodFlipLogo from "../../assets/icons/goodflip_logo.png";
import QuantityHelpImage from "../../assets/icons/quantity_help.png";
import {
  DIET_PLAN_PDF_COPY,
  DIET_PLAN_PDF_LIFESTYLE_TIPS,
  DIET_PLAN_PDF_MINDFUL_EATING_TIPS,
  DIET_PLAN_PDF_NUTRITION_TIPS,
} from "../../constants/diet";
import {
  CalendarIconPdf,
  CheckIconPdf,
  DiseaseIconPdf,
  HeightIconPdf,
  HotIconPdf,
  ProfileIconPdf,
  TipIconPdf,
  WeightIconPdf,
} from "./DietPlanPdfIcons";
import { formatDietTips } from "./formatDietTips";

const styles = StyleSheet.create({
  page: {
    paddingTop: 0,
    paddingBottom: 20,
    paddingHorizontal: 0,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: "#171725",
  },
  contentPad: {
    paddingHorizontal: 10,
  },
  header: {
    height: 45,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: 10,
    marginBottom: 0,
  },
  headerBg: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 45,
  },
  logo: {
    height: 27,
    width: 110,
    objectFit: "contain",
  },
  patientRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 20,
    paddingHorizontal: 10,
    width: "98%",
  },
  patientLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  patientRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  iconGap: {
    marginRight: 10,
  },
  patientName: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    color: "#171725",
  },
  patientMeta: {
    fontSize: 10,
    marginTop: 2,
  },
  labelTiny: {
    fontSize: 9,
    color: "#171725",
  },
  valueSmall: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#171725",
    marginTop: 1,
  },
  dietName: {
    backgroundColor: "#E6FAF1",
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    textAlign: "center",
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 6,
    marginTop: 20,
    width: "95%",
    alignSelf: "center",
  },
  metricsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    paddingHorizontal: 10,
    width: "94%",
    alignSelf: "center",
  },
  metricItem: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
    flexGrow: 1,
    flexBasis: 0,
    maxWidth: "25%",
  },
  metricIcon: {
    marginRight: 10,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#E2E2EA",
    marginTop: 30,
    marginHorizontal: 10,
  },
  mealsWrap: {
    marginTop: 25,
    paddingHorizontal: 10,
    width: "98%",
  },
  mealBlock: {
    marginTop: 20,
  },
  mealHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    minHeight: 19,
  },
  mealRibbonWrap: {
    width: 90,
    height: 19,
    position: "relative",
    justifyContent: "center",
  },
  mealRibbonText: {
    position: "absolute",
    left: 5,
    top: 3,
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#FFFFFF",
  },
  mealTiming: {
    fontSize: 9,
    color: "#171725",
    marginLeft: 10,
  },
  optionCard: {
    marginTop: 12,
  },
  optionTitleWrap: {
    width: "100%",
    alignItems: "center",
  },
  optionTitle: {
    backgroundColor: "#454551",
    color: "#FFFFFF",
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    textTransform: "uppercase",
    textAlign: "center",
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    width: "25%",
  },
  table: {
    width: "95%",
    alignSelf: "center",
    borderTopWidth: 1,
    borderTopColor: "#000000",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F1F1F5",
    paddingVertical: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E2EA",
    paddingVertical: 10,
  },
  thFood: {
    width: "50%",
    fontSize: 12,
    fontFamily: "Helvetica",
    paddingLeft: 4,
  },
  thQty: { width: "25%", fontSize: 12 },
  thUnit: { width: "25%", fontSize: 12 },
  tdFood: { width: "50%", fontSize: 12, paddingLeft: 4 },
  tdQty: { width: "25%", fontSize: 12 },
  tdUnit: { width: "25%", fontSize: 12 },
  tipRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 10,
    paddingLeft: 20,
    minHeight: 20,
  },
  tipIconGap: {
    marginRight: 10,
  },
  tipText: {
    fontSize: 9,
    fontFamily: "Helvetica-Bold",
    flex: 1,
  },
  tipsSection: {
    marginTop: 25,
    width: "95%",
    alignSelf: "center",
  },
  tipBox: {
    borderWidth: 1,
    borderColor: "#299D6B",
    borderRadius: 8,
    padding: 10,
    marginTop: 10,
  },
  tipBoxTitle: {
    fontFamily: "Helvetica-Bold",
    color: "#299D6B",
    fontSize: 10,
    marginBottom: 6,
  },
  tipItem: {
    fontSize: 10,
    marginBottom: 4,
    textAlign: "justify",
    lineHeight: 1.4,
    paddingLeft: 8,
  },
  quantityImageWrap: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8,
  },
  quantityImage: {
    width: "100%",
    maxWidth: 500,
  },
  grayDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#A2A2A8",
    marginTop: 20,
  },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    color: "#299D6B",
    fontSize: 10,
    marginTop: 10,
    marginBottom: 4,
  },
  bodyText: {
    fontSize: 10,
    textAlign: "justify",
    lineHeight: 1.4,
  },
});

type DietPlanReactPdfDocumentProps = {
  dietData: any;
  patientDetails?: any;
};

const visibleMeals = (dietData: any) =>
  (dietData?.diet_plan_meal_rel || []).filter(
    (meal: any) =>
      (meal?.hide_meal == "N" || !meal?.hide_meal) &&
      !isNaN(meal?.meal_options?.[0]?.option_no),
  );

const A4_WIDTH = 595.28;

const HeaderBar = () => (
  <View style={styles.header}>
    <Svg style={styles.headerBg} width={A4_WIDTH} height={45}>
      <Defs>
        <LinearGradient id="headerGrad" x1="0" y1="0" x2="1" y2="0">
          <Stop offset="0" stopColor="#289E6C" />
          <Stop offset="1" stopColor="#FFFFFF" />
        </LinearGradient>
      </Defs>
      <Rect x={0} y={0} width={A4_WIDTH} height={45} fill="url(#headerGrad)" />
    </Svg>
    <Image src={goodFlipLogo} style={styles.logo} />
  </View>
);

const MealTypeRibbon = ({ label }: { label: string }) => (
  <View style={styles.mealRibbonWrap}>
    <CheckIconPdf />
    <Text style={styles.mealRibbonText}>{label}</Text>
  </View>
);

export const DietPlanReactPdfDocument = ({
  dietData,
  patientDetails,
}: DietPlanReactPdfDocumentProps) => {
  const genderLabel = patientDetails?.gender === "M" ? "Male" : "Female";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderBar />

        <View style={styles.patientRow}>
          <View style={styles.patientLeft}>
            <View style={styles.iconGap}>
              <ProfileIconPdf size={28} />
            </View>
            <View>
              <Text style={styles.patientName}>{patientDetails?.name}</Text>
              <Text style={styles.patientMeta}>
                {genderLabel} • {patientDetails?.patient_age} yrs
              </Text>
            </View>
          </View>
          <View style={styles.patientRight}>
            <View style={styles.iconGap}>
              <CalendarIconPdf size={24} />
            </View>
            <View>
              <Text style={styles.labelTiny}>Date Range</Text>
              <Text style={styles.valueSmall}>
                {moment(dietData?.start_date).format("DD MMM YYYY")} -{" "}
                {moment(dietData?.end_date).format("DD MMM YYYY")}
              </Text>
            </View>
          </View>
        </View>

        <Text style={styles.dietName}>DIET NAME : {dietData?.plan_name}</Text>

        <View style={styles.metricsRow}>
          <View style={styles.metricItem}>
            <View style={styles.metricIcon}>
              <HeightIconPdf />
            </View>
            <View>
              <Text style={styles.labelTiny}>Height</Text>
              <Text style={styles.valueSmall}>
                {patientDetails?.height} {patientDetails?.height_unit || "cms"}
              </Text>
            </View>
          </View>
          <View style={styles.metricItem}>
            <View style={styles.metricIcon}>
              <WeightIconPdf />
            </View>
            <View>
              <Text style={styles.labelTiny}>Weight</Text>
              <Text style={styles.valueSmall}>
                {patientDetails?.weight} {patientDetails?.weight_unit || "kg"}
              </Text>
            </View>
          </View>
          <View style={styles.metricItem}>
            <View style={styles.metricIcon}>
              <DiseaseIconPdf />
            </View>
            <View>
              <Text style={styles.labelTiny}>Disease</Text>
              <Text style={styles.valueSmall}>
                {patientDetails?.medical_condition_name}
              </Text>
            </View>
          </View>
          <View style={styles.metricItem}>
            <View style={styles.metricIcon}>
              <HotIconPdf />
            </View>
            <View>
              <Text style={styles.labelTiny}>Total Calories Goal</Text>
              <Text style={styles.valueSmall}>
                {dietData?.total_calories}(kcal)
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.mealsWrap}>
          {visibleMeals(dietData).map((meal: any, mealIdx: number) => (
            <View key={`meal-${mealIdx}`} style={styles.mealBlock}>
              <View style={styles.mealHeader} wrap={false}>
                <MealTypeRibbon label={meal?.meal_type || ""} />
                <Text style={styles.mealTiming}>
                  Meal Timing : {`${meal?.start_time} - ${meal?.end_time}`}
                </Text>
              </View>

              {(meal?.meal_options || []).map((op: any, opIdx: number) => {
                const tipsText = formatDietTips(op?.option_data?.[0]?.tips);
                return (
                <View
                  key={`option-${mealIdx}-${opIdx}`}
                  style={styles.optionCard}
                >
                  <View style={styles.optionTitleWrap} wrap={false}>
                    <Text style={styles.optionTitle}>
                      {meal?.meal_type} {op?.option_no}
                    </Text>
                  </View>
                  <View style={styles.table}>
                    <View style={styles.tableHeader} wrap={false}>
                      <Text style={styles.thFood}>Food Item</Text>
                      <Text style={styles.thQty}>Quantity</Text>
                      <Text style={styles.thUnit}>Measurement</Text>
                    </View>
                    {(op?.option_data || []).map((data: any, idx: number) => (
                      <View
                        key={`food-${mealIdx}-${opIdx}-${idx}`}
                        style={styles.tableRow}
                        wrap={false}
                      >
                        <Text style={styles.tdFood}>{data?.food_name}</Text>
                        <Text style={styles.tdQty}>{data?.quantity}</Text>
                        <Text style={styles.tdUnit}>
                          {data?.unit_option_name}
                        </Text>
                      </View>
                    ))}
                  </View>
                  {tipsText ? (
                    <View style={styles.tipRow} wrap={false}>
                      <View style={styles.tipIconGap}>
                        <TipIconPdf />
                      </View>
                      <Text style={styles.tipText}>Tips : {tipsText}</Text>
                    </View>
                  ) : null}
                </View>
                );
              })}
            </View>
          ))}
        </View>

        <View style={styles.tipsSection}>
          <View style={styles.tipBox} wrap={false}>
            <Text style={styles.tipBoxTitle}>
              {DIET_PLAN_PDF_COPY.NUTRITION_TIPS_TITLE}
            </Text>
            {DIET_PLAN_PDF_NUTRITION_TIPS.map((tip, i) => (
              <Text key={`n-${i}`} style={styles.tipItem}>
                • {tip}
              </Text>
            ))}
          </View>

          <View style={styles.tipBox} wrap={false}>
            <Text style={styles.tipBoxTitle}>
              {DIET_PLAN_PDF_COPY.MINDFUL_EATING_TIPS_TITLE}
            </Text>
            {DIET_PLAN_PDF_MINDFUL_EATING_TIPS.map((tip, i) => (
              <Text key={`m-${i}`} style={styles.tipItem}>
                • {tip}
              </Text>
            ))}
          </View>

          <View style={styles.tipBox} wrap={false}>
            <Text style={styles.tipBoxTitle}>
              {DIET_PLAN_PDF_COPY.LIFESTYLE_TIPS_TITLE}
            </Text>
            {DIET_PLAN_PDF_LIFESTYLE_TIPS.map((tip, i) => (
              <Text key={`l-${i}`} style={styles.tipItem}>
                • {tip}
              </Text>
            ))}
          </View>

          <View style={styles.tipBox} wrap={false}>
            <Text style={styles.tipBoxTitle}>
              {DIET_PLAN_PDF_COPY.KITCHEN_MEASUREMENTS_TITLE}
            </Text>
            <View style={styles.quantityImageWrap}>
              <Image src={QuantityHelpImage} style={styles.quantityImage} />
            </View>
          </View>

          <View style={styles.grayDivider} />

          <Text style={styles.sectionTitle}>
            {DIET_PLAN_PDF_COPY.DISCLAIMER_TITLE}
          </Text>
          <Text style={styles.bodyText}>{DIET_PLAN_PDF_COPY.DISCLAIMER}</Text>

          <Text style={styles.sectionTitle}>
            {DIET_PLAN_PDF_COPY.IMPORTANT_NOTE_TITLE}
          </Text>
          <Text style={styles.bodyText}>
            {DIET_PLAN_PDF_COPY.IMPORTANT_NOTE}
          </Text>
          <Text style={[styles.bodyText, { marginTop: 10 }]}>
            {DIET_PLAN_PDF_COPY.IMPORTANT_NOTE_OCCASIONS}
          </Text>

          <View style={styles.grayDivider} />
        </View>
      </Page>
    </Document>
  );
};

export default DietPlanReactPdfDocument;
